/**
 * Step 0 — Story intro (Phase 2 task #2).
 *
 * Full-screen 5-slide Instagram-style carousel:
 *  - 5 slides, 5 detik each (25 detik total)
 *  - Instagram-style top progress segments (active segment animates
 *    0% → 100% over the slide duration)
 *  - Cross-fade between slides (200ms in/out)
 *  - "Lewati" button always visible (top-right) — replace → /welcome
 *  - "Mulai" button only on the last slide (bottom) — replace → /welcome
 *  - Auto-advance after the last slide completes its 5 detik
 *
 * Reference: plan §6.1 task #2, BST V4 §onboarding-flow
 *  Slide content per plan:
 *    1. Logo tagline
 *    2. "Halo! Kenalan yuk"
 *    3. "Ide baru setiap hari"
 *    4. "Tinggal swipe"
 *    5. "Tumbuh bersama si kecil"
 *
 * Reduced motion:
 *  - Progress fill snaps to 100% (no linear animation)
 *  - Cross-fade skipped (instant slide change)
 *  - Auto-advance still works at the same 5 detik interval
 */

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  Animated,
  ActivityIndicator,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { MISC_ICON } from '@/lib/icons';
import { getAuthToken, hasEverAuthenticated, isGuestSession } from '@/lib/auth';
import { ensureGuestSession, fetchMe } from '@/lib/api/auth';
import { getActiveChild } from '@/lib/api/children';
import { ApiError } from '@/lib/api/client';
import { trackAnalyticsEvent } from '@/lib/analytics';

import { ONBOARDING_ROUTES } from '@/constants/onboarding';

type Slide = {
  /** Hero icon key (MISC_ICON) — large, centered above the title. */
  icon: keyof typeof MISC_ICON;
  /** Slide title (1 line, brand purple, subtitle type). */
  title: string;
  /** Supporting copy (1-2 lines, muted, body type). */
  subtitle: string;
};

const SLIDES: readonly Slide[] = [
  {
    icon: 'books',
    title: 'Funpaper',
    subtitle: 'Ide aktivitas harian untuk si kecil, siap main setiap hari.',
  },
  {
    icon: 'sparkle',
    title: 'Halo!',
    subtitle: 'Kenalan yuk dengan Funpaper. Bikin belajar si kecil makin seru.',
  },
  {
    icon: 'cards',
    title: 'Ide baru setiap hari',
    subtitle: 'Rekomendasi aktivitas seru sesuai usia dan aktivitas favorit si kecil.',
  },
  {
    icon: 'swipe',
    title: 'Tinggal swipe',
    subtitle: 'Pilih aktivitas yang cocok, lewati yang kurang pas.',
  },
  {
    icon: 'star',
    title: 'Tumbuh bersama si kecil',
    subtitle: 'Sedikit waktu setiap hari, jadi momen bermain sambil belajar yang menyenangkan.',
  },
];

const SLIDE_DURATION_MS = 5_000;
const FADE_DURATION_MS = 200;
const SEGMENT_HEIGHT = 3;
const SEGMENT_GAP = 4;

function prefersReducedMotion(): boolean {
  return (
    Platform.OS === 'web' &&
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

export default function StoryIntroScreen() {
  const router = useRouter();
  const [isCheckingReturningUser, setIsCheckingReturningUser] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const fillAnim = useRef(new Animated.Value(0)).current;
  const reducedMotion = useRef(prefersReducedMotion()).current;

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const isTrialExpired = (iso: string | null) =>
        iso !== null && Date.parse(iso) <= Date.now();
      const goToLogin = () => router.replace('/login' as never);

      try {
        if (!getAuthToken()) {
          const boot = await ensureGuestSession();
          if (cancelled) return;
          // Server tahu browser ini pernah login akun asli (device_link)
          // atau trial guest-nya sudah habis → langsung ke /login, jangan
          // biarkan user mengulang onboarding lalu menabrak dead-end.
          if (boot.deviceLinked || isTrialExpired(boot.trialEndsAt)) {
            goToLogin();
            return;
          }
        }
        const child = await getActiveChild();
        if (cancelled) return;
        if (child) {
          router.replace('/home' as never);
          return;
        }
        // Sesi guest yang sudah menggantung tidak membawa trial_ends_at →
        // tanyakan ke server: trial habis atau device pernah login akun asli.
        if (isGuestSession()) {
          try {
            const me = await fetchMe();
            if (cancelled) return;
            if (me.deviceLinked || isTrialExpired(me.trialEndsAt)) {
              goToLogin();
              return;
            }
          } catch (err) {
            if (err instanceof ApiError && err.code === 'TrialExpired') {
              goToLogin();
              return;
            }
            // Network error — lanjut ke onboarding seperti perilaku lama.
          }
        }
        // User lama yang pernah login akun asli di browser ini (mis. setelah
        // logout) tidak disambut onboarding lagi — antar langsung ke login.
        if (hasEverAuthenticated()) {
          goToLogin();
          return;
        }
      } catch {
        // First launch can continue into onboarding if bootstrap/check fails.
      }
      if (!cancelled) {
        trackAnalyticsEvent('Onboarding Started');
        setIsCheckingReturningUser(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [router]);

  useEffect(() => {
    if (isCheckingReturningUser) return;

    // Reset and animate the active progress segment.
    fillAnim.setValue(0);
    if (!reducedMotion) {
      Animated.timing(fillAnim, {
        toValue: 1,
        duration: SLIDE_DURATION_MS,
        easing: Easing.linear,
        useNativeDriver: false,
      }).start();
    } else {
      fillAnim.setValue(1);
    }

    // Auto-advance: after SLIDE_DURATION_MS, either fade to the next slide
    // or (on the last slide) replace → /welcome.
    const isLast = activeIndex >= SLIDES.length - 1;
    const timer = setTimeout(() => {
      if (isLast) {
        trackAnalyticsEvent('Onboarding Step Completed', { step_name: 'story_intro' });
        router.replace(ONBOARDING_ROUTES.welcome);
        return;
      }
      if (reducedMotion) {
        setActiveIndex((i) => i + 1);
        return;
      }
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: FADE_DURATION_MS,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) {
          setActiveIndex((i) => i + 1);
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: FADE_DURATION_MS,
            useNativeDriver: true,
          }).start();
        }
      });
    }, SLIDE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [activeIndex, fadeAnim, fillAnim, isCheckingReturningUser, reducedMotion, router]);

  const onLewati = () => {
    trackAnalyticsEvent('Onboarding Step Completed', { step_name: 'story_intro' });
    router.replace(ONBOARDING_ROUTES.welcome);
  };
  const onMulai = () => {
    trackAnalyticsEvent('Onboarding Step Completed', { step_name: 'story_intro' });
    router.replace(ONBOARDING_ROUTES.welcome);
  };

  const slide = SLIDES[activeIndex];
  const isLast = activeIndex === SLIDES.length - 1;
  const fillWidth = fillAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  if (isCheckingReturningUser) {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'bottom', 'left', 'right']}>
          <View style={styles.loading} accessibilityLabel="Memuat Funpaper">
            <ActivityIndicator color={Brand.primary} />
            <ThemedText type="default" themeColor="textSecondary">
              Memuat Funpaper…
            </ThemedText>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  // Defensive: if activeIndex is somehow out of bounds (e.g. after
  // unmount race), render nothing rather than crash.
  if (!slide) {
    return null;
  }

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'bottom', 'left', 'right']}>
        {/* Instagram-style top progress segments */}
        <View style={styles.progressRow} accessibilityLabel="Story progress">
          {SLIDES.map((_, i) => (
            <View
              key={i}
              style={styles.segment}
              accessibilityRole="progressbar"
            >
              {i < activeIndex ? (
                <View style={[styles.segmentFill, { width: '100%' }]} />
              ) : i === activeIndex ? (
                <Animated.View style={[styles.segmentFill, { width: fillWidth }]} />
              ) : null}
            </View>
          ))}
        </View>

        {/* Lewati button */}
        <View style={styles.topRow}>
          <View style={styles.spacer} />
          <Pressable
            onPress={onLewati}
            hitSlop={12}
            accessibilityRole="button"
            accessibilityLabel="Lewati story"
          >
            <ThemedText type="linkPrimary" style={styles.lewatiText}>
              Lewati ›
            </ThemedText>
          </Pressable>
        </View>

        {/* Slide content (cross-fades between slides) */}
        <Animated.View style={[styles.slide, { opacity: fadeAnim }]}>
          <AppIcon source={MISC_ICON[slide.icon]} size={120} />
          <ThemedText type="subtitle" style={styles.title}>
            {slide.title}
          </ThemedText>
          <ThemedText
            type="default"
            themeColor="textSecondary"
            style={styles.subtitle}
          >
            {slide.subtitle}
          </ThemedText>
        </Animated.View>

        {/* Mulai CTA — only on the last slide */}
        {isLast ? (
          <View style={styles.bottom}>
            <Pressable
              onPress={onMulai}
              accessibilityRole="button"
              style={styles.cta}
            >
              <ThemedText type="default" style={styles.ctaText}>
                Mulai
              </ThemedText>
            </Pressable>
          </View>
        ) : null}
      </SafeAreaView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  safe: {
    flex: 1,
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.two,
  },
  progressRow: {
    flexDirection: 'row',
    gap: SEGMENT_GAP,
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
  },
  segment: {
    flex: 1,
    height: SEGMENT_HEIGHT,
    backgroundColor: 'rgba(86, 63, 122, 0.18)',
    borderRadius: SEGMENT_HEIGHT / 2,
    overflow: 'hidden',
  },
  segmentFill: {
    height: '100%',
    backgroundColor: Brand.primary,
    borderRadius: SEGMENT_HEIGHT / 2,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.two,
    minHeight: 44,
  },
  spacer: {
    minWidth: 88,
  },
  lewatiText: {
    fontSize: 14,
    fontWeight: '500',
  },
  slide: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  title: {
    textAlign: 'center',
    color: Brand.primary,
  },
  subtitle: {
    textAlign: 'center',
    maxWidth: 320,
  },
  bottom: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.four,
  },
  cta: {
    backgroundColor: Brand.primary,
    paddingVertical: Spacing.three,
    paddingHorizontal: Spacing.four,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 4,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});
